// Card primitives — the atomic unit of the design language (User Journeys
// & Service Design v2: every navigation option, resource, feature, and menu
// entry is one of these).
//
//   Card    — a plain white content container. Replaces the ad-hoc
//             `rounded-lg border border-border p-6` divs scattered across
//             the app so radius / border / padding stay consistent.
//   NavCard — the tinted-icon-tile + title + one-line subtitle + chevron
//             card. Renders as a router Link (`to`), an anchor (`href`),
//             a button (`onClick`), or a static div. `prominent` is the
//             heavier primary-action treatment; `meta` swaps the chevron
//             for a right-aligned status/count node.
//
// Surface-aware: the tile / title / chevron use `*-primary*` tokens, so a
// NavCard inside `data-surface="employee"` renders blue automatically.

import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronRightIcon } from '@/components/ui/icons.tsx';

export function Card({
  children,
  className,
  as: As = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'article';
}) {
  return (
    <As className={['rounded-lg border border-border bg-card p-6', className ?? ''].join(' ')}>
      {children}
    </As>
  );
}

interface NavCardProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  /** Router link target. */
  to?: string;
  /** External / anchor target (takes precedence styling as a link). */
  href?: string;
  onClick?: () => void;
  /** Heavier primary-action treatment (the Employee "I just need to talk" card). */
  prominent?: boolean;
  /** Title colour. Entry/option cards use `primary`; content cards use `foreground`. */
  titleTone?: 'primary' | 'foreground';
  /** Right-aligned node (status chip / count) shown instead of the chevron. */
  meta?: ReactNode;
  className?: string;
}

export function NavCard({
  title,
  subtitle,
  icon,
  to,
  href,
  onClick,
  prominent = false,
  titleTone = 'primary',
  meta,
  className,
}: NavCardProps) {
  const shell = prominent
    ? 'min-h-[112px] gap-5 rounded-2xl border-2 border-primary bg-primary-soft p-5 hover:bg-primary-soft/70 focus-visible:ring-primary/60'
    : 'min-h-[88px] gap-4 rounded-xl border border-primary-soft-border bg-card p-4 hover:border-primary/50 hover:bg-primary-soft/40 focus-visible:ring-primary/40';
  const tile = prominent
    ? 'size-16 rounded-2xl bg-primary text-primary-foreground'
    : 'size-12 rounded-xl bg-primary-soft text-primary';
  const titleCls = [
    prominent ? 'text-lg font-semibold' : 'text-base font-semibold',
    titleTone === 'primary' ? 'text-primary' : 'text-foreground',
  ].join(' ');

  const inner = (
    <>
      {icon ? (
        <span aria-hidden className={['grid shrink-0 place-items-center', tile].join(' ')}>
          {icon}
        </span>
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={titleCls}>{title}</span>
        {subtitle ? <span className="text-sm text-muted-foreground">{subtitle}</span> : null}
      </span>
      {meta ?? (
        <span
          aria-hidden
          className={[
            'shrink-0 transition [&_svg]:size-4',
            prominent
              ? 'text-primary group-hover:translate-x-0.5'
              : 'text-muted-foreground/60 group-hover:text-primary',
          ].join(' ')}
        >
          <ChevronRightIcon />
        </span>
      )}
    </>
  );

  const classes = [
    'group flex items-center transition focus-visible:ring-2 focus-visible:outline-none',
    shell,
    className ?? '',
  ].join(' ');

  if (to) {
    return (
      <Link to={to} className={classes}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes}>
        {inner}
      </a>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={['w-full text-left', classes].join(' ')}>
        {inner}
      </button>
    );
  }
  return <div className={classes}>{inner}</div>;
}
